export interface HeroStat {
  num: string;
  label: string;
}

export interface Benefit {
  icon: string;
  label: string;
  desc: string;
}

export interface EventItem {
  title: string;
  date: string;
  spots: string;
  hot: boolean;
}

export interface ContactInfo {
  wechat: string;
  phone: string;
  email: string;
  address: string;
  hours: string;
}

export interface LandingEventPhoto {
  seq: string;
  label: string;
  src: string;
}

export interface LandingTestimonial {
  name: string;
  avatar: string;
  text: string;
  color: string;
}

/** 欢迎页固定区块文案（与 Logo 一并来自落地页接口，便于单点运维）。 */
export interface LandingPageUi {
  /** 预览框状态栏时间等装饰性展示 */
  statusBarTime: string;
  tabBenefits: string;
  tabEvents: string;
  sectionGalleryTitle: string;
  sectionGallerySubtitle: string;
  sectionTestimonialsTitle: string;
  sectionContactTitle: string;
  contactQrHint: string;
  contactWechatLabel: string;
  contactPhoneLabel: string;
  contactHoursPrefix: string;
  browseButton: string;
  welcomeBackTitle: string;
  /** 可用占位符 {orgName}，运行时替换为 orgName */
  welcomeBackSubtitle: string;
  memberRoleBadge: string;
  guestRoleLabel: string;
  memberRoleLabel: string;
  eventHotLabel: string;
}

export interface LandingPageConfig {
  orgName: string;
  /** 顶栏与会员欢迎动效用的协会 Logo 地址 */
  logoUrl: string;
  slogan: string;
  subSlogan: string;
  heroDesc: string;
  heroImage: string;
  /** 顶部主视觉下小徽章文案 */
  heroBadge: string;
  joinGuide: string;
  joinButtonText: string;
  stats: HeroStat[];
  benefits: Benefit[];
  events: EventItem[];
  contact: ContactInfo;
  eventPhotos: LandingEventPhoto[];
  testimonials: LandingTestimonial[];
  ui: LandingPageUi;
}

/** 未配置落地页时的空壳（不向终端展示演示文案）。 */
/** 旧版 JSON 无 ui 字段时的中文默认，避免页面缺字；仍以接口返回覆盖非空项。 */
export const DEFAULT_LANDING_PAGE_UI: LandingPageUi = {
  statusBarTime: "9:41",
  tabBenefits: "会员专属权益",
  tabEvents: "近期精彩活动",
  sectionGalleryTitle: "精彩活动回顾",
  sectionGallerySubtitle: "历年活动瞬间",
  sectionTestimonialsTitle: "球迷说",
  sectionContactTitle: "联系我们",
  contactQrHint: "扫码关注",
  contactWechatLabel: "微信公众号",
  contactPhoneLabel: "联系电话",
  contactHoursPrefix: "工作时间",
  browseButton: "先去逛逛 →",
  welcomeBackTitle: "欢迎回来",
  welcomeBackSubtitle: "{orgName} · 会员专区",
  memberRoleBadge: "正式会员",
  guestRoleLabel: "游客",
  memberRoleLabel: "会员",
  eventHotLabel: "热门",
};

export const EMPTY_LANDING_PAGE: LandingPageConfig = {
  orgName: "",
  logoUrl: "",
  slogan: "",
  subSlogan: "",
  heroDesc: "",
  heroImage: "",
  heroBadge: "",
  joinGuide: "",
  joinButtonText: "",
  stats: [],
  benefits: [],
  events: [],
  contact: { wechat: "", phone: "", email: "", address: "", hours: "" },
  eventPhotos: [],
  testimonials: [],
  ui: { ...DEFAULT_LANDING_PAGE_UI },
};

function isLandingPageUiPartial(x: unknown): x is Record<string, unknown> {
  return typeof x === "object" && x !== null && !Array.isArray(x);
}

function mergeLandingPageUi(raw: unknown): LandingPageUi {
  const base = { ...DEFAULT_LANDING_PAGE_UI };
  if (!isLandingPageUiPartial(raw)) return base;
  const keys = Object.keys(DEFAULT_LANDING_PAGE_UI) as (keyof LandingPageUi)[];
  for (const k of keys) {
    const v = raw[k as string];
    if (typeof v === "string") base[k] = v;
  }
  return base;
}

function isHeroStat(x: unknown): x is HeroStat {
  return typeof x === "object" && x !== null && "num" in x && "label" in x;
}

function isBenefit(x: unknown): x is Benefit {
  return typeof x === "object" && x !== null && "icon" in x && "label" in x && "desc" in x;
}

function isEventItem(x: unknown): x is EventItem {
  return typeof x === "object" && x !== null && "title" in x && "date" in x && "spots" in x && "hot" in x;
}

function isContact(x: unknown): x is ContactInfo {
  return (
    typeof x === "object" &&
    x !== null &&
    "wechat" in x &&
    "phone" in x &&
    "email" in x &&
    "address" in x &&
    "hours" in x
  );
}

function isEventPhoto(x: unknown): x is LandingEventPhoto {
  return typeof x === "object" && x !== null && "seq" in x && "label" in x && "src" in x;
}

function isTestimonial(x: unknown): x is LandingTestimonial {
  return typeof x === "object" && x !== null && "name" in x && "avatar" in x && "text" in x && "color" in x;
}

/** 与 API 返回合并；无数据时用空壳，不回填演示内容。 */
export function mergeLandingPage(raw: unknown): LandingPageConfig {
  const e = EMPTY_LANDING_PAGE;
  if (!raw || typeof raw !== "object") {
    return {
      ...e,
      contact: { ...e.contact },
    };
  }
  const o = raw as Record<string, unknown>;
  const stats = Array.isArray(o.stats) ? o.stats.filter(isHeroStat) : [];
  const benefits = Array.isArray(o.benefits) ? o.benefits.filter(isBenefit) : [];
  const events = Array.isArray(o.events) ? o.events.filter(isEventItem) : [];
  const contact = isContact(o.contact) ? o.contact : null;
  const eventPhotos = Array.isArray(o.eventPhotos) ? o.eventPhotos.filter(isEventPhoto) : [];
  const testimonials = Array.isArray(o.testimonials) ? o.testimonials.filter(isTestimonial) : [];

  return {
    orgName: typeof o.orgName === "string" ? o.orgName : e.orgName,
    logoUrl: typeof o.logoUrl === "string" ? o.logoUrl : e.logoUrl,
    slogan: typeof o.slogan === "string" ? o.slogan : e.slogan,
    subSlogan: typeof o.subSlogan === "string" ? o.subSlogan : e.subSlogan,
    heroDesc: typeof o.heroDesc === "string" ? o.heroDesc : e.heroDesc,
    heroImage: typeof o.heroImage === "string" ? o.heroImage : e.heroImage,
    heroBadge: typeof o.heroBadge === "string" ? o.heroBadge : e.heroBadge,
    joinGuide: typeof o.joinGuide === "string" ? o.joinGuide : e.joinGuide,
    joinButtonText: typeof o.joinButtonText === "string" ? o.joinButtonText : e.joinButtonText,
    stats,
    benefits,
    events,
    contact: contact ?? { ...e.contact },
    eventPhotos,
    testimonials,
    ui: mergeLandingPageUi(o.ui),
  };
}
